import {Dispatch, SetStateAction} from 'react'
import cn from 'clsx'
import {useParams} from 'react-router-dom'
import {Space} from 'antd'

import styles from './ProjectDrawdownsList.module.scss'
import useStore from 'base/store'
import {
  Box,
  Block,
  ListHeader,
  Text,
  Circle,
  ArrowShortSecondIcon,
} from 'common/components'
import {formatMoney, formatDate} from 'common/helpers'
import {
  DrawdownTag,
  useGetDrawdowns,
  DrawdownItem,
} from '..'
import { DRAWDOWNSTATUS, DRAWDOWN_REQUEST_STATUS_NAME } from 'modules/request/constants.drawdown'

type SetDrawdownUuid = Dispatch<SetStateAction<string>>

interface ProjectDrawdownsListProps {
  drawdownUuid: string
  setDrawdownUuid: SetDrawdownUuid
}

interface DrawdownProps {
  isBorrower: boolean
  selected: boolean
  drawdown: DrawdownItem
  setDrawdownUuid: SetDrawdownUuid
}

export default function ProjectDrawdownsList({
  drawdownUuid,
  setDrawdownUuid,
}: ProjectDrawdownsListProps) {
  const params = useParams()
  const role = useStore((state) => state.role)
  const {data: drawdowns} = useGetDrawdowns(params.uuid)

  return (
    <div>
      <Head />
      <ul className={styles.list}>
        {drawdowns.map((drawdown) => (
          <Drawdown
            key={drawdown.uuid}
            isBorrower={role.user}
            drawdown={drawdown}
            selected={drawdownUuid === drawdown.uuid}
            setDrawdownUuid={setDrawdownUuid}
          />
        ))}
      </ul>
    </div>
  )
}

const Head = () => (
  <ListHeader className={styles.grid}>
    <div />
    <Space>
      <span>#</span>
      <span>Drawdown</span>
    </Space>
    <Box align="right">Request</Box>
    <Box align="center">Status</Box>
    <Box align="right">APPROVED</Box>
    {/* <Box align="right">Withdrawn, nzd</Box> */}
  </ListHeader>
)

const Drawdown = ({
  isBorrower,
  selected,
  drawdown,
  setDrawdownUuid,
}: DrawdownProps) => {
  const handleSelect = () => setDrawdownUuid(drawdown.uuid)

  const textColor = drawdown.status === 'declined' ? 'grey' : undefined

  return (
    <Block
      as="li"
      className={cn(styles.grid, selected && styles.selected)}
      type="grey"
      onClick={handleSelect}
    >
      {/* 
        空心[非borrower 并且 非declined 并且 非declined]
        decline - 灰色  非borrower角色 - 黑色  borrower: 蓝绿色
      */}
      <Box align="left">
        <Circle
          border={isBorrower}
          hole={
            !isBorrower &&
            drawdown.status !== DRAWDOWNSTATUS.RECEIVED &&
            drawdown.status !== DRAWDOWNSTATUS.DECLINED
          }
          color={
            drawdown.status === DRAWDOWNSTATUS.DECLINED
              ? 'grey'
              : isBorrower
              ? drawdown.status ===  DRAWDOWNSTATUS.RECEIVED
                ? 'darkTurquoize'
                : 'lightTurquoize'
              : 'black'
          }
        />
      </Box>
      <Space>
        <Text color="grey" size="xs" align="right">
          {drawdown.sortOrder}
        </Text>
        <Text weight="demiBold" color={textColor} align='left'>
          {drawdown.title}
        </Text>
      </Space>
      <Box align="right">
        <Text color={textColor} mb={2}>
          {formatMoney(drawdown.requestedAmount, {cents: true})}
        </Text>
        <Text size="xs" color="grey">
          {formatDate(drawdown.createdAt, 'sixth')}
        </Text>
      </Box>
      <Box align="center">
        {drawdown.status === DRAWDOWNSTATUS.RECEIVED ? (
          <>
            <Text uppercase spacing weight="bold" size="3xs" mb={2}>
              {DRAWDOWN_REQUEST_STATUS_NAME.received}
            </Text>
            <Text size="xs" color="grey">
              {formatDate(drawdown.updatedAt, 'sixth')}
            </Text>
          </>
        ) : (
          <DrawdownTag
            transparent
            noIcon
            isBorrower={isBorrower}
            status={drawdown.status}
          />
        )}
      </Box>
      <Text
        align="right"
        weight={selected ? 'demiBold' : undefined}
        color={textColor}
      >
        {formatMoney(drawdown.approvedAmount || drawdown.requestedAmount, {
          cents: true,
        })}
      </Text>
      <Box align="right" color="black30">
        {!selected && <ArrowShortSecondIcon />}
      </Box>
    </Block>
  )
}
